// M3b.6 — TypeScript port of briefing_room_backend/engine/resolution.py.
//
// The Python file (E1b.2, 2026-06-01) implements 3 pure functions + a
// top-level resolve_task. Both Python + TS read the same canonical-data
// tables; the source-of-truth is the JSON, not either codebase. This port
// exists so the frontend can compute Primary Chance / Success Chance /
// Quality Rating in-browser without a backend round-trip (per M3b.6 prep
// brief §4.1 user-approved default).
//
// Test fixtures in __tests__/resolution.test.ts mirror Python's
// test_resolution.py shape using a fixed-roll lambda for determinism.
//
// Source-of-truth manifest:
//   docs/GAMES/RPG/007 JAMES BOND RPG/DOCS/20260601_CORE_RULEBOOK_AUTHORITATIVE_MANIFEST.md
//
// Canonical Core Rulebook citations:
//   - PC formulas: p.31 SKILL FORMULAS (→ seed/skills.json)
//   - SC = PC × EF: p.158 MULTIPLICATION TABLE (→ seed/tables/multiplication_table.json)
//   - QR brackets: p.157 QUALITY RESULTS TABLE (→ seed/tables/quality_results_table.json)
//
// The TS implementation uses the FORMULA-based fast path (matching
// resolution.py:116-129 + :135-181). The JSON tables remain canonical
// regression sources for ground-truth pinning.

// Ship 2 refactor: canonical enumerations + skill lookup live in `./tables`
// (single source of truth, JSON-backed). Re-exported here so existing callers
// that import EASE_FACTORS/QR_NAMES/EaseFactor/Characteristic from this
// module continue to compile.
import { EASE_FACTORS, QR_NAMES, getSkillDefinition } from './tables';
import type { Characteristic, EaseFactor } from './tables';

export { EASE_FACTORS, QR_NAMES };
export type { Characteristic, EaseFactor };

// ─── Character context ──────────────────────────────────────────────────

/** Mirrors engine/resolution.py:31-65 CharacterContext dataclass. */
export interface CharacterContext {
  strength: number;
  dexterity: number;
  willpower: number;
  perception: number;
  intelligence: number;
  skill_levels: Record<string, number>;
}

export function getSkillLevel(ctx: CharacterContext, skillName: string): number {
  return ctx.skill_levels[skillName] ?? 0;
}

export function getCharacteristic(ctx: CharacterContext, code: Characteristic): number {
  const m: Record<Characteristic, number> = {
    STR: ctx.strength,
    DEX: ctx.dexterity,
    WIL: ctx.willpower,
    PER: ctx.perception,
    INT: ctx.intelligence,
  };
  return m[code];
}

// ─── Primary Chance (mirrors resolution.py:71-110) ──────────────────────

export function computePrimaryChance(
  skillName: string,
  character: CharacterContext,
): number {
  const skill = getSkillDefinition(skillName);
  const level = getSkillLevel(character, skillName);

  switch (skill.formula_pattern) {
    case 'add_then_skill_level': {
      if (skill.characteristics.length !== 1) {
        throw new Error(
          `Skill ${JSON.stringify(skillName)} has add_then pattern but ${skill.characteristics.length} characteristics`,
        );
      }
      return getCharacteristic(character, skill.characteristics[0]!) + level;
    }
    case 'avg_then_add_skill_level': {
      if (skill.characteristics.length !== 2) {
        throw new Error(
          `Skill ${JSON.stringify(skillName)} has avg_then pattern but ${skill.characteristics.length} characteristics`,
        );
      }
      const c1 = getCharacteristic(character, skill.characteristics[0]!);
      const c2 = getCharacteristic(character, skill.characteristics[1]!);
      return Math.floor((c1 + c2) / 2) + level;
    }
    case 'wil_plus_charisma_skill_level_avg_then_add_skill_level': {
      // Seduction special case per Core p.31 GM Note.
      const wil = character.willpower;
      const charismaLevel = getSkillLevel(character, 'Charisma');
      return Math.floor((wil + charismaLevel) / 2) + level;
    }
    default: {
      // Exhaustiveness — unreachable if SkillFormulaPattern stays in sync with skills.json.
      const exhaustive: never = skill.formula_pattern;
      throw new Error(`Unknown formula_pattern: ${String(exhaustive)} for skill ${JSON.stringify(skillName)}`);
    }
  }
}

// ─── Success Chance (mirrors resolution.py:116-129) ─────────────────────

export function computeSuccessChance(pc: number, ef: EaseFactor): number {
  if (!Number.isInteger(pc) || pc < 1 || pc > 30) {
    throw new Error(`Primary Chance must be 1-30, got ${pc}`);
  }
  // EF type narrows at compile time, but if input came from JSON / user
  // data the runtime guard catches drift:
  if (!(EASE_FACTORS as readonly string[]).includes(ef)) {
    throw new Error(
      `Ease Factor must be one of ${EASE_FACTORS.join(', ')}, got ${JSON.stringify(ef)}`,
    );
  }
  if (ef === '0.5') {
    return Math.max(1, Math.floor(pc / 2));
  }
  return pc * Number(ef);
}

// ─── Quality Rating (mirrors resolution.py:135-181) ─────────────────────

export function computeQualityRating(sc: number, roll: number): 1 | 2 | 3 | 4 | null {
  if (!Number.isInteger(sc) || sc < 1) {
    throw new Error(`Success Chance must be >= 1, got ${sc}`);
  }
  if (!Number.isInteger(roll) || roll < 1 || roll > 100) {
    throw new Error(`d100 roll must be 1-100, got ${roll}`);
  }

  // Core p.157 verbatim: "A dice result of 100 is always a failure."
  if (roll === 100) return null;
  if (roll > sc) return null;

  let rangeUpper: number;
  if (sc <= 10) {
    rangeUpper = 10;
  } else {
    // Mirror Python's ((sc + 9) // 10) * 10 — round-up to next 10.
    rangeUpper = Math.ceil(sc / 10) * 10;
  }

  const qr1Max = Math.floor(rangeUpper / 10);
  const qr2Max = Math.floor(rangeUpper / 5);
  const qr3Max =
    sc > 100
      ? Math.min(Math.floor(rangeUpper / 2), 98)
      : Math.floor(rangeUpper / 2);
  const qr4Max = Math.min(sc, 99);

  if (roll <= qr1Max) return 1;
  if (roll <= qr2Max) return 2;
  if (roll <= qr3Max) return 3;
  if (roll <= qr4Max) return 4;
  return null;
}

// ─── Resolve task (top-level; mirrors resolution.py:212-236) ────────────

export interface ResolutionResult {
  success: boolean;
  quality_rating: 1 | 2 | 3 | 4 | null;
  quality_name: string;
  primary_chance: number;
  ease_factor: EaseFactor;
  success_chance: number;
  d100_roll: number;
}

function defaultRoll(): number {
  // 1..100 inclusive — mirrors Python's random.randint(1, 100).
  return Math.floor(Math.random() * 100) + 1;
}

// ─── Quality Rating brackets (2026-06-07) ───────────────────────────────

/**
 * Returns the d100 roll ranges that map to each Quality Rating for a
 * given Success Chance. Mirrors the bracket logic in
 * `computeQualityRating` so the SPA can preview the difficulty (e.g.
 * "QR 1 = roll 1–13 (13%), QR 2 = 14–26 (13%) …") before the player
 * commits to a roll.
 *
 * Returns `null` if SC is out of range (< 1). Each bracket entry's
 * `chancePercent` is the integer percent of rolls that land in that
 * bracket — useful for surfacing difficulty inline.
 */
export interface QualityRatingBracket {
  qr: 1 | 2 | 3 | 4;
  qrName: string;
  /** Lowest d100 roll that yields this QR (inclusive). */
  low: number;
  /** Highest d100 roll that yields this QR (inclusive). */
  high: number;
  /** Integer percent chance of landing in this bracket (1..100). */
  chancePercent: number;
}

export interface QualityRatingBracketBreakdown {
  brackets: QualityRatingBracket[];
  /** Inclusive roll range that fails (always includes 100, may exclude
   *  none if sc >= 99). */
  failureLow: number;
  failureHigh: number;
  failureChancePercent: number;
}

export function computeQualityRatingBrackets(sc: number): QualityRatingBracketBreakdown | null {
  if (!Number.isInteger(sc) || sc < 1) return null;
  const rangeUpper = sc <= 10 ? 10 : Math.ceil(sc / 10) * 10;
  // Raw qr boundaries per Core p.157. Each bracket must also be capped at
  // SC (since `computeQualityRating` short-circuits to Failure when
  // `roll > sc`) — without the cap, low-SC brackets over-count and the
  // total tile doesn't add to 100%.
  const qr4Max = Math.min(sc, 99);
  const qr3MaxRaw =
    sc > 100 ? Math.min(Math.floor(rangeUpper / 2), 98) : Math.floor(rangeUpper / 2);
  const qr2MaxRaw = Math.floor(rangeUpper / 5);
  const qr1MaxRaw = Math.floor(rangeUpper / 10);

  const brackets: QualityRatingBracket[] = [];
  let cursor = 0;
  const advance = (qr: 1 | 2 | 3 | 4, rawHigh: number) => {
    const high = Math.min(rawHigh, qr4Max);
    const low = cursor + 1;
    if (high < low) return;
    brackets.push({
      qr,
      qrName: QR_NAMES[qr],
      low,
      high,
      chancePercent: high - low + 1,
    });
    cursor = high;
  };
  advance(1, qr1MaxRaw);
  advance(2, qr2MaxRaw);
  advance(3, qr3MaxRaw);
  advance(4, qr4Max);
  // Failure range: everything past the last bracket through 100. Always
  // includes 100 (Core p.157: "A dice result of 100 is always a failure.").
  const failureLow = cursor + 1;
  const failureHigh = 100;
  const failureChancePercent = failureHigh - failureLow + 1;
  return {
    brackets,
    failureLow,
    failureHigh,
    failureChancePercent,
  };
}

export function resolveTask(
  pc: number,
  ef: EaseFactor,
  rollFn: () => number = defaultRoll,
): ResolutionResult {
  const sc = computeSuccessChance(pc, ef);
  const roll = rollFn();
  if (!Number.isInteger(roll) || roll < 1 || roll > 100) {
    throw new Error(`rollFn must return integer 1..100, got ${roll}`);
  }
  const qr = computeQualityRating(sc, roll);
  return {
    success: qr !== null,
    quality_rating: qr,
    quality_name: qr === null ? 'Failure' : QR_NAMES[qr],
    primary_chance: pc,
    ease_factor: ef,
    success_chance: sc,
    d100_roll: roll,
  };
}
